import { styled } from '@mui/material/styles';
import type {
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import { DataTableContext, useDataTableContext } from '@/features/DataGrid';
import type { RowHeights } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import type { ColumnsConfig } from '@/shared/types';
import type { TotalData } from '@/shared/ui';
import { DataGrid as SharedDataGrid, useDataGridListeners } from '@/shared/ui';
import type { DataResponse, Highlight } from '../../model/types';
import { getRowHeight } from './getRowHeight';
import { useData } from './useData';
import { useTableProps } from './useTableProps';

const DataGrid = styled(SharedDataGrid)(({ theme }) => ({
  '& .dimensionCell + .metricCell, & .dimensionHeader + .metricHeader': {
    boxShadow: 'inset 8px 0 8px -8px #00234033',
  },

  '& .dimensionCell': {
    padding: `0 ${theme.spacing(1.25)}`,
    flexShrink: 0,
  },

  '& .columnGroup': {
    display: 'none',
  },
}));

interface Props {
  columnsWidth: Record<string, number | undefined>;
  columnsConfig: ColumnsConfig;
  rowHeight: RowHeights;
  dimensions: string[];
  metric: string;
  highlight: Highlight;
  getRowsPage: (offset?: number) => Promise<DataResponse>;
  getTotal: () => Promise<TotalData>;
  reorderDimensions: (info: {
    id: string;
    oldIndex: number;
    newIndex: number;
  }) => void;
  updateColumnsWidth: (columnName: string, newWidth?: number) => void;
}

function Content({
  columnsWidth,
  columnsConfig,
  rowHeight,
  dimensions,
  metric,
  highlight,
  getRowsPage,
  getTotal,
  reorderDimensions,
  updateColumnsWidth,
}: Props) {
  const {
    initialState,
    detailPanelExpandedRowIds,
    getDetailPanelHeight,
    getDetailPanelContent,
  } = useDataTableContext();
  const {
    total,
    rows,
    columns: dataColumns,
    loading,
    getNextPage,
  } = useData({
    getTotal,
    getRowsPage,
  });
  const { columnGroupingModel, columns } = useTableProps({
    total,
    rowHeight,
    columnsWidth,
    columnsConfig,
    dimensions,
    metric,
    dataColumns,
  });

  useDataGridListeners();

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const { column } = params;
    // Need to substract 1 from indexes due to hidden row details column
    const oldIndex = params.oldIndex - 1;
    const targetIndex = params.targetIndex - 1;

    if (oldIndex === targetIndex) {
      return;
    }

    reorderDimensions({
      id: column.field,
      oldIndex: oldIndex,
      newIndex: targetIndex,
    });
    Analytics.sendFeatureUsage('data_table', 'reorder_column', {
      page: window.location.pathname,
    });
  };
  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const { width, field } = params.colDef;

    updateColumnsWidth(field, width);
  };

  return (
    <DataGrid
      getRowHeight={getRowHeight({ rowHeight, highlight })}
      initialState={initialState}
      loading={loading}
      detailPanelExpandedRowIds={detailPanelExpandedRowIds}
      getDetailPanelHeight={getDetailPanelHeight}
      getDetailPanelContent={getDetailPanelContent}
      columnGroupingModel={columnGroupingModel}
      columns={columns}
      rows={rows}
      getRowClassName={(params) => {
        return params.row.isTotal ? 'total' : '';
      }}
      onRowsScrollEnd={getNextPage}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
}

export function DataTable(props: Props) {
  return (
    <DataTableContext pageFetcher={props.getRowsPage}>
      <Content {...props} />
    </DataTableContext>
  );
}
